/**
 * Matriz GUT — Gravidade × Urgência × Tendência.
 *
 * CONECTADO NA ONDA 2. `criticality` é chamada por
 * `ms-core/src/modules/nonconformity/nonconformity.service.ts` em dois
 * pontos: na abertura da NC (GUT do EMISSOR → `criticality_score`) e na
 * avaliação assinada do gestor (GUT do GESTOR → `manager_criticality_score`),
 * reproduzindo as duas colunas separadas do legado (`erp_nc.criticidade` e
 * `erp_nc.manager_rate`, sgq.md §A.2.2 e §A.3).
 *
 * Os limiares vêm de `organization.gut_low_max` / `gut_medium_max` — a decisão
 * de onde cortar é do SGQ do cliente, não nossa, e o legado tinha as duas faixas
 * conflitantes descritas abaixo cravadas em código.
 *
 * PARIDADE COM O LEGADO (docs/legacy-spec/sgq.md)
 * ------------------------------------------------
 * O legado tinha DUAS faixas de criticidade conflitantes para a mesma fórmula:
 *   - na grade de NC:  ≤5 / 6-10 / >10
 *   - no resto do sistema: 1-20 / 21-40 / ≥41
 *
 * Como o produto de três fatores de 1 a 5 varia de 1 a 125, a faixa "≤5 / 6-10 / >10"
 * classificaria quase tudo como crítico. A faixa 1-20 / 21-40 / ≥41 é a que
 * corresponde à prática usual da ferramenta e a que preservamos, deixando os
 * limiares CONFIGURÁVEIS por organização — a decisão de onde cortar é do SGQ
 * do cliente, não nossa.
 *
 * Divergência registrada em docs/divergences.md#D-GUT-01.
 */
export const GUT_MIN = 1;
export const GUT_MAX = 5;
export const CriticalityLevel = {
    LOW: 'LOW',
    MEDIUM: 'MEDIUM',
    HIGH: 'HIGH',
};
export const CRITICALITY_LABEL_PT_BR = {
    LOW: 'Baixa',
    MEDIUM: 'Média',
    HIGH: 'Alta',
};
export const DEFAULT_CRITICALITY_THRESHOLDS = {
    lowMax: 20,
    mediumMax: 40,
};
export class InvalidGutScoreError extends Error {
    constructor(field, value) {
        super(`${field} precisa ser um inteiro entre ${GUT_MIN} e ${GUT_MAX}. Recebido: ${value}.`);
        this.name = 'InvalidGutScoreError';
    }
}
function assertFactor(field, value) {
    if (!Number.isInteger(value) || value < GUT_MIN || value > GUT_MAX) {
        throw new InvalidGutScoreError(field, value);
    }
}
/** Produto G × U × T. Varia de 1 a 125. */
export function gutScore(score) {
    assertFactor('Gravidade', score.severity);
    assertFactor('Urgência', score.urgency);
    assertFactor('Tendência', score.trend);
    return score.severity * score.urgency * score.trend;
}
export function criticalityFromScore(score, thresholds = DEFAULT_CRITICALITY_THRESHOLDS) {
    if (score <= thresholds.lowMax)
        return CriticalityLevel.LOW;
    if (score <= thresholds.mediumMax)
        return CriticalityLevel.MEDIUM;
    return CriticalityLevel.HIGH;
}
export function criticality(score, thresholds) {
    const value = gutScore(score);
    return { score: value, level: criticalityFromScore(value, thresholds) };
}
//# sourceMappingURL=gut.js.map